import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import {
  PieChart, Pie, Cell, BarChart, Bar,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
} from 'recharts';

const API = process.env.REACT_APP_API_URL || 'http://localhost:8000';

interface Txn {
  id: number;
  transaction_date: string;
  amount: string | number;
  transaction_type: 'DEBIT' | 'CREDIT';
  payment_mode: string;
  particular?: string | null;
  ledger_category_name?: string | null;
  ledger_subcategory_name?: string | null;
}

const COLORS = ['#ef4444', '#f97316', '#eab308', '#8b5cf6', '#06b6d4', '#ec4899', '#14b8a6', '#3b82f6', '#84cc16', '#f43f5e'];
const OTHER_COLOR = '#cbd5e1';

// ── helpers ──
function getAuthHeader() {
  const token = localStorage.getItem('accessToken');
  return token ? { Authorization: `Bearer ${token}` } : {};
}
const pad2 = (n: number) => String(n).padStart(2, '0');
const ymOf = (d: Date) => `${d.getFullYear()}-${pad2(d.getMonth() + 1)}`;
const addMonth = (ym: string, delta: number) => {
  const [y, m] = ym.split('-').map(Number);
  const idx = y * 12 + (m - 1) + delta;
  return `${Math.floor(idx / 12)}-${pad2((idx % 12) + 1)}`;
};
const monthsEndingAt = (ym: string, n: number) => Array.from({ length: n }, (_, i) => addMonth(ym, -(n - 1 - i)));
const labelOf = (ym: string) => {
  const [y, m] = ym.split('-').map(Number);
  return new Date(y, m - 1, 1).toLocaleDateString('en-IN', { month: 'short', year: 'numeric' });
};
const firstDay = (ym: string) => `${ym}-01`;
const lastDay = (ym: string) => {
  const [y, m] = ym.split('-').map(Number);
  return `${ym}-${pad2(new Date(y, m, 0).getDate())}`;
};
const fmtL = (n: number) => {
  const a = Math.abs(n);
  if (a >= 100000) return `₹${(n / 100000).toFixed(2)}L`;
  if (a >= 1000) return `₹${(n / 1000).toFixed(1)}K`;
  return `₹${n.toFixed(0)}`;
};
const fmtFull = (n: number) => (n < 0 ? '-' : '') + '₹' + Math.round(Math.abs(n)).toLocaleString('en-IN');
const amt = (t: Txn) => Number(t.amount) || 0;

function aggBy(list: Txn[], key: (t: Txn) => string) {
  const m: Record<string, number> = {};
  list.forEach((t) => { const k = key(t) || 'Uncategorised'; m[k] = (m[k] || 0) + amt(t); });
  const total = Object.values(m).reduce((a, b) => a + b, 0);
  return Object.entries(m)
    .map(([name, value]) => ({ name, value, percent: total > 0 ? (value / total) * 100 : 0 }))
    .sort((a, b) => b.value - a.value);
}

const PieTip = ({ active, payload }: any) => {
  if (!active || !payload?.length) return null;
  const p = payload[0];
  return (
    <div style={{ background: 'white', border: '1px solid #e2e8f0', borderRadius: 8, padding: '0.6rem 0.9rem', boxShadow: '0 4px 12px rgba(0,0,0,0.1)', fontSize: '0.82rem' }}>
      <div style={{ fontWeight: 700 }}>{p.name}</div>
      <div style={{ color: '#64748b' }}>{fmtFull(p.value)} ({p.payload.percent?.toFixed(1)}%)</div>
    </div>
  );
};
const BarTip = ({ active, payload, label }: any) => {
  if (!active || !payload?.length) return null;
  return (
    <div style={{ background: 'white', border: '1px solid #e2e8f0', borderRadius: 8, padding: '0.7rem 0.9rem', boxShadow: '0 4px 12px rgba(0,0,0,0.1)', fontSize: '0.82rem' }}>
      <div style={{ fontWeight: 700, marginBottom: 4 }}>{label}</div>
      {payload.filter((p: any) => p.value).map((p: any) => (
        <div key={p.name} style={{ display: 'flex', gap: 6, alignItems: 'center', marginBottom: 2 }}>
          <div style={{ width: 10, height: 10, borderRadius: 2, background: p.color || p.fill }} />
          <span style={{ color: '#64748b' }}>{p.name}:</span><span style={{ fontWeight: 600 }}>{fmtFull(p.value)}</span>
        </div>
      ))}
    </div>
  );
};

const WINDOW = 6;

const SpendAnalyser: React.FC = () => {
  const [selectedMonth, setSelectedMonth] = useState(ymOf(new Date()));
  const [txns, setTxns] = useState<Txn[]>([]);
  const [loading, setLoading] = useState(true);

  const windowMonths = useMemo(() => monthsEndingAt(selectedMonth, WINDOW), [selectedMonth]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const start = firstDay(windowMonths[0]);
      const end = lastDay(selectedMonth);
      const res = await axios.get(`${API}/api/financial/transactions`, {
        params: { date_from: start, date_to: `${end}T23:59:59`, transaction_type: 'DEBIT' },
        headers: getAuthHeader(),
      });
      setTxns(Array.isArray(res.data) ? res.data : []);
    } catch {
      toast.error('Failed to load spend data');
    } finally {
      setLoading(false);
    }
  }, [windowMonths, selectedMonth]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const inMonth = (ym: string) => txns.filter((t) => String(t.transaction_date).slice(0, 7) === ym);
  const monthTxns = useMemo(() => inMonth(selectedMonth), [txns, selectedMonth]); // eslint-disable-line react-hooks/exhaustive-deps
  const prevYm = addMonth(selectedMonth, -1);
  const prevTxns = useMemo(() => inMonth(prevYm), [txns, prevYm]); // eslint-disable-line react-hooks/exhaustive-deps

  const totalSpend = monthTxns.reduce((s, t) => s + amt(t), 0);
  const prevSpend = prevTxns.reduce((s, t) => s + amt(t), 0);
  const spendDeltaPct = prevSpend > 0 ? ((totalSpend - prevSpend) / prevSpend) * 100 : null;

  const byCategory = useMemo(() => aggBy(monthTxns, (t) => t.ledger_category_name || 'Uncategorised'), [monthTxns]);
  const bySub = useMemo(() => aggBy(monthTxns, (t) => t.ledger_subcategory_name || '—'), [monthTxns]);
  const byMode = useMemo(() => aggBy(monthTxns, (t) => t.payment_mode || 'OTHER'), [monthTxns]);
  const biggest = useMemo(() => [...monthTxns].sort((a, b) => amt(b) - amt(a)).slice(0, 8), [monthTxns]);

  // Category trend (stacked) over the window — top 5 categories + Other
  const { trendData, trendCats } = useMemo(() => {
    const totals = aggBy(txns, (t) => t.ledger_category_name || 'Uncategorised');
    const top = totals.slice(0, 5).map((c) => c.name);
    const data = windowMonths.map((ym) => {
      const row: Record<string, number | string> = { label: labelOf(ym) };
      top.forEach((c) => (row[c] = 0));
      row.Other = 0;
      inMonth(ym).forEach((t) => {
        const c = t.ledger_category_name || 'Uncategorised';
        const key = top.includes(c) ? c : 'Other';
        row[key] = (row[key] as number) + amt(t);
      });
      return row;
    });
    return { trendData: data, trendCats: [...top, 'Other'] };
  }, [txns, windowMonths]); // eslint-disable-line react-hooks/exhaustive-deps

  // Movers — category change vs previous month
  const movers = useMemo(() => {
    const cur: Record<string, number> = {}; const prev: Record<string, number> = {};
    monthTxns.forEach((t) => { const c = t.ledger_category_name || 'Uncategorised'; cur[c] = (cur[c] || 0) + amt(t); });
    prevTxns.forEach((t) => { const c = t.ledger_category_name || 'Uncategorised'; prev[c] = (prev[c] || 0) + amt(t); });
    const names = Array.from(new Set([...Object.keys(cur), ...Object.keys(prev)]));
    return names
      .map((name) => ({ name, cur: cur[name] || 0, prev: prev[name] || 0, change: (cur[name] || 0) - (prev[name] || 0) }))
      .filter((r) => Math.abs(r.change) > 0.5)
      .sort((a, b) => Math.abs(b.change) - Math.abs(a.change))
      .slice(0, 8);
  }, [monthTxns, prevTxns]);

  const hasData = monthTxns.length > 0;
  const curLabel = labelOf(selectedMonth);
  const atCurrentMonth = selectedMonth >= ymOf(new Date());
  const navBtn = (disabled: boolean): React.CSSProperties => ({
    padding: '0.35rem 0.65rem', borderRadius: 8, border: '1px solid #e2e8f0', background: 'white',
    cursor: disabled ? 'not-allowed' : 'pointer', fontSize: '1rem', lineHeight: 1, color: disabled ? '#cbd5e1' : '#475569',
  });

  const s: Record<string, React.CSSProperties> = {
    page: { padding: '1.75rem', maxWidth: 1300, margin: '0 auto', fontFamily: '"Inter", sans-serif' },
    header: { display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: '1.25rem', flexWrap: 'wrap', gap: '1rem' },
    title: { fontSize: '1.5rem', fontWeight: 700, color: '#0f172a' },
    sub: { fontSize: '0.85rem', color: '#64748b', marginTop: 2 },
    input: { padding: '0.45rem 0.6rem', borderRadius: 8, border: '1px solid #e2e8f0', fontSize: '0.85rem', background: 'white' },
    card: { background: 'white', borderRadius: 12, padding: '1.25rem 1.5rem', boxShadow: '0 1px 4px rgba(0,0,0,0.07)' },
    cardTitle: { fontSize: '0.9rem', fontWeight: 700, color: '#0f172a', marginBottom: '1rem' },
    kpiGrid: { display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '1rem', marginBottom: '1.5rem' },
    row2: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.25rem', marginBottom: '1.5rem' },
  };

  const Kpi = ({ label, value, color, sub }: { label: string; value: string; color: string; sub?: React.ReactNode }) => (
    <div style={{ ...s.card, borderLeft: `4px solid ${color}`, padding: '1.1rem 1.4rem' }}>
      <div style={{ fontSize: '0.76rem', fontWeight: 600, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6 }}>{label}</div>
      <div style={{ fontSize: '1.5rem', fontWeight: 800, color, lineHeight: 1.1 }}>{value}</div>
      {sub && <div style={{ fontSize: '0.76rem', marginTop: 6 }}>{sub}</div>}
    </div>
  );

  return (
    <div style={s.page}>
      <div style={s.header}>
        <div>
          <div style={s.title}>Spend Analyser</div>
          <div style={s.sub}>Where the money goes — by category, subcategory &amp; payment mode</div>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <label style={{ fontSize: '0.8rem', color: '#64748b' }}>Month</label>
          <button style={navBtn(false)} title="Previous month" onClick={() => setSelectedMonth(addMonth(selectedMonth, -1))}>‹</button>
          <input type="month" value={selectedMonth} max={ymOf(new Date())}
            onChange={(e) => e.target.value && setSelectedMonth(e.target.value)} style={s.input} />
          <button style={navBtn(atCurrentMonth)} title="Next month" disabled={atCurrentMonth}
            onClick={() => setSelectedMonth(addMonth(selectedMonth, 1))}>›</button>
        </div>
      </div>

      {loading && <div style={{ textAlign: 'center', padding: '4rem', color: '#94a3b8' }}>Loading…</div>}

      {!loading && (
        <>
          <div style={s.kpiGrid}>
            <Kpi label="Total Spend" value={fmtL(totalSpend)} color="#ef4444"
              sub={spendDeltaPct == null ? <span style={{ color: '#94a3b8' }}>no prior month</span> :
                <span style={{ color: spendDeltaPct <= 0 ? '#16a34a' : '#dc2626', fontWeight: 600 }}>
                  {spendDeltaPct <= 0 ? '▼' : '▲'} {Math.abs(spendDeltaPct).toFixed(1)}% <span style={{ color: '#94a3b8', fontWeight: 400 }}>vs {labelOf(prevYm)}</span>
                </span>} />
            <Kpi label="Transactions" value={String(monthTxns.length)} color="#3b82f6" />
            <Kpi label="Avg / Transaction" value={fmtL(monthTxns.length ? totalSpend / monthTxns.length : 0)} color="#8b5cf6" />
            <Kpi label="Top Category" value={byCategory[0]?.name || '—'} color="#f97316"
              sub={byCategory[0] ? <span style={{ color: '#64748b' }}>{fmtFull(byCategory[0].value)} ({byCategory[0].percent.toFixed(0)}%)</span> : undefined} />
          </div>

          {!hasData ? (
            <div style={{ ...s.card, padding: '3rem', textAlign: 'center', color: '#94a3b8' }}>
              No spend (ledger DEBIT) recorded in {curLabel}. Add ledger entries and they'll show up here.
            </div>
          ) : (
            <>
              <div style={s.row2}>
                {/* Donut by category */}
                <div style={s.card}>
                  <div style={s.cardTitle}>Spend by Category — {curLabel}</div>
                  <ResponsiveContainer width="100%" height={280}>
                    <PieChart>
                      <Pie data={byCategory} dataKey="value" nameKey="name" cx="50%" cy="50%" innerRadius={60} outerRadius={100} paddingAngle={2}>
                        {byCategory.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                      </Pie>
                      <Tooltip content={<PieTip />} />
                      <Legend wrapperStyle={{ fontSize: '0.78rem' }} />
                    </PieChart>
                  </ResponsiveContainer>
                </div>

                {/* Ranked category bars */}
                <div style={s.card}>
                  <div style={s.cardTitle}>Categories ranked</div>
                  <ResponsiveContainer width="100%" height={280}>
                    <BarChart data={byCategory} layout="vertical" margin={{ top: 4, right: 16, left: 8, bottom: 4 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" horizontal={false} />
                      <XAxis type="number" tickFormatter={(v) => fmtL(v)} tick={{ fontSize: 10, fill: '#94a3b8' }} axisLine={false} tickLine={false} />
                      <YAxis type="category" dataKey="name" width={110} tick={{ fontSize: 11, fill: '#64748b' }} axisLine={false} tickLine={false} />
                      <Tooltip content={<BarTip />} cursor={{ fill: '#f8fafc' }} />
                      <Bar dataKey="value" name="Spend" radius={[0, 4, 4, 0]} maxBarSize={22}>
                        {byCategory.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>

              <div style={s.row2}>
                {/* Payment mode donut */}
                <div style={s.card}>
                  <div style={s.cardTitle}>By Payment Mode — {curLabel}</div>
                  <ResponsiveContainer width="100%" height={260}>
                    <PieChart>
                      <Pie data={byMode} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={95} paddingAngle={2}>
                        {byMode.map((_, i) => <Cell key={i} fill={COLORS[(i + 3) % COLORS.length]} />)}
                      </Pie>
                      <Tooltip content={<PieTip />} />
                      <Legend wrapperStyle={{ fontSize: '0.78rem' }} />
                    </PieChart>
                  </ResponsiveContainer>
                </div>

                {/* Biggest single spends */}
                <div style={s.card}>
                  <div style={s.cardTitle}>Biggest spends — {curLabel}</div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    {biggest.map((t) => (
                      <div key={t.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0.5rem 0', borderBottom: '1px solid #f1f5f9' }}>
                        <div style={{ minWidth: 0 }}>
                          <div style={{ fontSize: '0.84rem', fontWeight: 600, color: '#0f172a', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {t.particular || t.ledger_subcategory_name || t.ledger_category_name || 'Spend'}
                          </div>
                          <div style={{ fontSize: '0.72rem', color: '#94a3b8' }}>
                            {t.ledger_category_name || '—'} · {new Date(t.transaction_date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' })}
                          </div>
                        </div>
                        <div style={{ fontSize: '0.86rem', fontWeight: 700, color: '#ef4444', marginLeft: 12 }}>{fmtFull(amt(t))}</div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Category trend (stacked) */}
              <div style={{ ...s.card, marginBottom: '1.5rem' }}>
                <div style={s.cardTitle}>Category trend — last {WINDOW} months</div>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={trendData} margin={{ top: 8, right: 16, left: 8, bottom: 4 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                    <XAxis dataKey="label" tick={{ fontSize: 11, fill: '#64748b' }} axisLine={false} tickLine={false} />
                    <YAxis tickFormatter={(v) => fmtL(v)} tick={{ fontSize: 11, fill: '#94a3b8' }} axisLine={false} tickLine={false} width={64} />
                    <Tooltip content={<BarTip />} cursor={{ fill: '#f8fafc' }} />
                    <Legend wrapperStyle={{ fontSize: '0.78rem', paddingTop: '0.5rem' }} />
                    {trendCats.map((c, i) => (
                      <Bar key={c} dataKey={c} name={c} stackId="a" maxBarSize={40}
                        fill={c === 'Other' ? OTHER_COLOR : COLORS[i % COLORS.length]} />
                    ))}
                  </BarChart>
                </ResponsiveContainer>
              </div>

              {/* Movers + subcategories */}
              <div style={s.row2}>
                <div style={s.card}>
                  <div style={s.cardTitle}>Movers — {curLabel} vs {labelOf(prevYm)}</div>
                  {movers.length === 0 ? (
                    <div style={{ color: '#94a3b8', fontSize: '0.85rem', padding: '1rem 0' }}>No change vs previous month.</div>
                  ) : movers.map((m) => (
                    <div key={m.name} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0.5rem 0', borderBottom: '1px solid #f1f5f9' }}>
                      <div style={{ fontSize: '0.84rem', fontWeight: 600, color: '#0f172a' }}>{m.name}</div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        <span style={{ fontSize: '0.74rem', color: '#94a3b8' }}>{fmtL(m.prev)} → {fmtL(m.cur)}</span>
                        <span style={{ fontSize: '0.82rem', fontWeight: 700, color: m.change > 0 ? '#dc2626' : '#16a34a', minWidth: 64, textAlign: 'right' }}>
                          {m.change > 0 ? '▲' : '▼'} {fmtL(Math.abs(m.change))}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>

                <div style={s.card}>
                  <div style={s.cardTitle}>Top subcategories — {curLabel}</div>
                  {bySub.slice(0, 8).map((sc, i) => (
                    <div key={sc.name} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0.5rem 0', borderBottom: '1px solid #f1f5f9' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <div style={{ width: 10, height: 10, borderRadius: 2, background: COLORS[i % COLORS.length] }} />
                        <span style={{ fontSize: '0.84rem', fontWeight: 600, color: '#0f172a' }}>{sc.name}</span>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        <span style={{ fontSize: '0.74rem', color: '#94a3b8' }}>{sc.percent.toFixed(0)}%</span>
                        <span style={{ fontSize: '0.84rem', fontWeight: 700, color: '#0f172a', minWidth: 70, textAlign: 'right' }}>{fmtFull(sc.value)}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
};

export default SpendAnalyser;
